/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import { useCallback, useEffect, useRef, useState } from 'react';
// Calls apiClient directly rather than adding a qrLoginService.js — this hook is
// single-purpose and Profile-page-scoped, matching Profile.jsx's existing deliberate
// deviation from the service-layer pattern (see docs/agent/modules/profile.md).
import apiClient from '../api/client';

const REFRESH_MARGIN_MS = 500;
const DEFAULT_DURATION_SECONDS = 60;
const MS_PER_SECOND = 1000;

// POST /auth/qr/generate picks the QR source by the user's account (see the server's
// qr-login adapters): a 60s ServiceHub token by default, or a 15-minute Pixo authToken for
// Pixo-enabled accounts. `available: false` means no QR can be shown (e.g. the user didn't
// sign in with Pixo) — `unavailable.message` is shown instead and nothing auto-refreshes.
const useQrLogin = () => {
  const [qrToken, setQrToken] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(DEFAULT_DURATION_SECONDS);
  const [provider, setProvider] = useState(null);
  const [unavailable, setUnavailable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refreshTimeoutRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  // Monotonic counter identifying the "current" generate() call. A boolean
  // mounted/active flag is NOT enough here: under React StrictMode's dev-mode
  // double-invoke of the mount effect, the orphaned first call's continuation
  // resolves while the hook is still mounted (the second call already re-armed
  // any such flag), so it would still be treated as valid — and whichever of
  // the two calls' network responses lands *last* would win, overwriting the
  // interval/timeout refs with a stale chain and leaking the other as an
  // uncleared, perpetually-ticking orphan (the actual cause of the countdown
  // toggling between two independent cycles, including on later auto-refreshes,
  // since one interval is never reachable via the refs again to clear it).
  // Capturing this call's counter value before any await and comparing it
  // against the ref's *current* value on resume reliably tells a call whether
  // it has been superseded, regardless of resolution order.
  const generationRef = useRef(0);

  const clearTimers = () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const generate = useCallback(async () => {
    const myGeneration = ++generationRef.current;
    clearTimers();
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.post('/auth/qr/generate');
      if (myGeneration !== generationRef.current) { return; } // superseded — discard

      setProvider(data.provider || null);
      if (data.available === false) {
        setUnavailable({ reason: data.reason, message: data.message });
        setQrToken(null);
        setExpiresAt(null);
        setSecondsRemaining(0);
        return;
      }
      setUnavailable(null);

      const durationSeconds = Number(data.durationSeconds) > 0
        ? Number(data.durationSeconds)
        : DEFAULT_DURATION_SECONDS;
      const parsedExpiry = new Date(data.expiresAt);
      const expiry = Number.isNaN(parsedExpiry.getTime())
        ? new Date(Date.now() + durationSeconds * MS_PER_SECOND)
        : parsedExpiry;

      setQrToken(data.qrToken);
      setExpiresAt(expiry);
      setTotalSeconds(durationSeconds);

      const msRemaining = expiry.getTime() - Date.now();
      setSecondsRemaining(Math.max(0, Math.round(msRemaining / 1000)));

      countdownIntervalRef.current = setInterval(() => {
        const remaining = Math.max(0, Math.round((expiry.getTime() - Date.now()) / 1000));
        setSecondsRemaining(remaining);
      }, 1000);

      refreshTimeoutRef.current = setTimeout(() => {
        generate();
      }, Math.max(0, msRemaining + REFRESH_MARGIN_MS));
    } catch (err) {
      if (myGeneration !== generationRef.current) { return; }
      setError(err.response?.data?.message || 'Failed to generate QR code');
      setUnavailable(null);
      setQrToken(null);
      setExpiresAt(null);
    } finally {
      if (myGeneration === generationRef.current) { setLoading(false); }
    }
  }, []);

  useEffect(() => {
    generate();
    return () => {
      generationRef.current += 1; // invalidate any in-flight call from this effect instance
      clearTimers();
    };
  }, [generate]);

  return {
    qrToken,
    expiresAt,
    secondsRemaining,
    totalSeconds,
    provider,
    unavailable,
    loading,
    error,
    regenerate: generate,
  };
};

export default useQrLogin;
